import { useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Modal, ModalBody } from "react-bootstrap";
import { yupResolver } from "@hookform/resolvers/yup";
import { isEmpty } from "lodash";
import { ModalHeader } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import EmptyData from "../../../components/EmptyData";
import SearchTable from "../../../components/SearchTable";
import ErrorMessage from "../../../components/ErrorMessage";
import AvatarCropper from "../../../components/AvatarCropper";
import UserAvatar from "../../../components/UserAvatar";
import Badge from "../../../components/Badge";
import useUploadFile from "../../../hook/useUploadFile";
import TableFilter from "../../../components/TableFilter";
import DataExporter from "../../../components/DataExporter";
import Icon from "../../../components/Icon";
import { areYouSureMessage } from "../../../utils/toastMessage/toastMessage";
import { categoryAdminContext } from "../../../context/AdminContext/CategoryAdminContext/CategoryAdminContext";
import { createCategoryValidation } from "../../../utils/validation/validation";
import { convertDate } from "../../../utils";
import { convertRoleToPath, exporterData } from "../../../utils/enum";

const CategoriesAdmin = () => {
  const { handleUploadFile } = useUploadFile();

  const {
    setSubCategories,
    category,
    setCategory,
    id,
    isShowModal,
    sort,
    setSort,
    start,
    setStart,
    end,
    setEnd,
    isActive,
    setIsActive,
    handleHideModal,
    handleGetCategories,
    handleCreateCategory,
    handleEditCategory,
  } = useContext(categoryAdminContext);

  useEffect(() => {
    handleGetCategories();
  }, [start, end, sort, isActive]);

  // Validation
  const {
    watch,
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(createCategoryValidation),
  });

  return (
    <>
      <SiteTitle title="دسته بندی ها" />

      <TableFilter
        setSort={setSort}
        sort={sort}
        setStart={setStart}
        start={start}
        setEnd={setEnd}
        end={end}
        setIsActive={setIsActive}
        isActive={isActive}
      />

      <TableContent setValue={setValue} reset={reset} />

      <Modal
        show={isShowModal}
        onHide={() => handleHideModal()}
        className="modal-dialog-centered"
        size="md"
      >
        <ModalHeader closeButton>
          <span className="fs-5 fw-bold">
            {id ? "ویرایش دسته بندی" : "افزودن دسته بندی جدید"}
          </span>
        </ModalHeader>
        <ModalBody>
          <div className="mb-40">
            <AvatarCropper
              onRemove={() => setValue("avatar", "")}
              value={watch("avatar")}
              onChange={(file) =>
                handleUploadFile(file, (e) => setValue("avatar", e), 1)
              }
            />
          </div>
          <form
            autoComplete="off"
            onSubmit={handleSubmit(
              id ? handleEditCategory : handleCreateCategory
            )}
          >
            <div className="row">
              <div className="col-12">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="title"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      عنوان
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      id="title"
                      placeholder="عنوان دسته بندی را وارد نمایید"
                      {...register("title")}
                    />
                  </div>

                  <ErrorMessage message={errors.title?.message} />
                </div>
              </div>

              <div className="col-12">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="description"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      توضیحات
                    </label>
                    <textarea
                      name="description"
                      className="form-control radius-8"
                      id="description"
                      rows={5}
                      placeholder="توضیحات دسته بندی  ..."
                      {...register("description")}
                    />
                  </div>

                  <ErrorMessage message={errors.description?.message} />
                </div>
              </div>
            </div>

            <div className="text-end mt-3">
              <button
                type="submit"
                className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
              >
                {id ? "ویرایش" : "افزودن"} دسته بندی
              </button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      <Modal
        show={!isEmpty(category)}
        onHide={() => {
          setSubCategories([]);
          setCategory({});
        }}
        className="modal-dialog-centered"
        size="xl"
      >
        <ModalHeader closeButton>
          <span className="fs-5 fw-bold">
            مدیریت دسته بندی های {category.title}
          </span>
        </ModalHeader>
        <ModalBody>
          <TableContent setValue={setValue} reset={reset} isSubCategories />
        </ModalBody>
      </Modal>
    </>
  );
};

export default CategoriesAdmin;

const TableContent = ({ reset, setValue, isSubCategories }) => {
  const navigate = useNavigate();

  const auth = useSelector((state) => state.auth);

  const {
    filter,
    setFilter,
    categories,
    subCategories,
    handleShowModal,
    handleGetCategories,
    handleDeleteCategory,
    handleChangeCategoryActive,
  } = useContext(categoryAdminContext);

  const items = isSubCategories ? subCategories : categories;

  return (
    <div className="card h-100 p-0 radius-12">
      <SearchTable
        endItem={
          <div className="d-flex justify-content-between align-items-center gap-2">
            {!isEmpty(items) && (
              <DataExporter
                headers={exporterData.categories.headers}
                keys={exporterData.categories.keys}
                data={items}
                fileName={exporterData.categories.fileName}
              />
            )}
            <button
              className="btn btn-primary text-sm btn-sm radius-8 d-flex align-items-center gap-2"
              onClick={() => {
                handleShowModal();
                reset();
              }}
            >
              <Icon icon="ic:baseline-plus" className="text-xl" />
              افزودن دسته بندی
            </button>
          </div>
        }
        filter={filter}
        setFilter={setFilter}
        onSearch={handleGetCategories}
      />
      <div className="card-body p-24">
        {isEmpty(items) ? (
          <EmptyData />
        ) : (
          <div className="table-responsive scroll-sm">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">عنوان</th>
                  <th scope="col">وضعیت</th>
                  <th scope="col">تاریخ ایجاد</th>
                  <th scope="col">تاریخ آخرین ویرایش</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr>
                    <td>{index + 1}</td>
                    <td>
                      {" "}
                      <div className="d-flex align-items-center">
                        <UserAvatar
                          className="ms-2"
                          {...(item?.avatar
                            ? { image: item?.avatar }
                            : {
                                icon: "solar:box-minimalistic-broken",
                              })}
                        />
                        <div className="flex-grow-1">
                          <span className="text-md mb-0 fw-normal text-secondary-light">
                            {item.title}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <Badge color={item.isActive ? "success" : "danger"}>
                        {item.isActive ? "فعال" : "غیرفعال"}
                      </Badge>
                    </td>
                    <td>{convertDate(item.createdAt, "YYYY/MM/DD - HH:mm")}</td>
                    <td>{convertDate(item.updatedAt, "YYYY/MM/DD - HH:mm")}</td>
                    <td>
                      <div className="dropdown">
                        <button
                          className="hover-bg-transparent"
                          type="button"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                        >
                          <Icon
                            icon="ph:dots-three-outline-fill"
                            className="menu-icon"
                          />
                        </button>
                        <ul className="dropdown-menu list">
                          <li
                            onClick={() =>
                              navigate(
                                `/${convertRoleToPath(
                                  auth.role
                                )}/discount-code/${item._id}/category`
                              )
                            }
                            className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                          >
                            گزارش کدهای تخفیف دسته بندی
                          </li>
                          {!isSubCategories && (
                            <li
                              onClick={() => handleGetCategories(null, item)}
                              className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                            >
                              زیر دسته ها
                            </li>
                          )}
                          <li
                            onClick={() =>
                              navigate(
                                `/${convertRoleToPath(auth.role)}/products/${
                                  item._id
                                }/category`
                              )
                            }
                            className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                          >
                            محصولات
                          </li>
                          <li
                            onClick={() => {
                              reset();
                              handleShowModal(item, setValue);
                            }}
                            className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                          >
                            ویرایش
                          </li>

                          <li className="dropdown-divider" />

                          <li
                            onClick={() =>
                              areYouSureMessage(
                                () => handleChangeCategoryActive(item._id),
                                `آیا از ${
                                  !item.isActive ? "فعال" : "غیرفعال"
                                } کردن دسته بندی ${item.title} مطمئنید؟`
                              )
                            }
                            className={`dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer text-${
                              !item.isActive ? "success" : "danger"
                            }`}
                          >
                            {!item.isActive ? "فعال" : "غیرفعال"}
                          </li>

                          <li
                            onClick={() =>
                              areYouSureMessage(
                                () => handleDeleteCategory(item._id),
                                `آیا از حذف دسته بندی ${item.title} مطمئنید؟`
                              )
                            }
                            className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer text-danger"
                          >
                            حذف
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
